import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleOutlined, InfoCircleOutlined, CaretRightOutlined, CaretDownOutlined } from '@ant-design/icons';
import { ProjectConfig } from '../../../types/deployApp.type';
import AITemplateGeneratorStep from '../../../components/deploy/AITemplateGeneratorStep';

export type ProjectTypeItem = {
  id: string;
  name: string;
  icon: React.ReactNode;
  badge?: string;
  description?: string;
};

export interface Step2DeployAppProps {
  config: ProjectConfig;
  setConfig: React.Dispatch<React.SetStateAction<ProjectConfig>>;
  selectedLogsPlan: string | null;
  setSelectedLogsPlan: React.Dispatch<React.SetStateAction<string | null>>;
  getRuntimeTechnology: (type: string) => string;
  getRuntimeVersions: (technology: string) => Array<{ value: string; label: string; recommended?: boolean }>;
  isSSRTechnology: (type: string, framework: string) => boolean;
  isSSGTechnology: (type: string, framework: string) => boolean;
  backendProjectTypes: ProjectTypeItem[];
}

// ─── Preset definitions ───────────────────────────────────

interface StartPreset {
  key: string;
  label: string;
  description: string;
  startMode: string;
  defaultTarget: string;
  targetLabel: string;
  targetPlaceholder: string;
  targetHint: string;
  buildDefault: string;
  preview: (target: string) => string;
}

const NODE_PRESETS: StartPreset[] = [
  {
    key: 'npm',
    label: 'npm run [script]',
    description: 'Ejecuta un script definido en package.json (lo más común)',
    startMode: 'npm',
    defaultTarget: 'start',
    targetLabel: 'Nombre del script',
    targetPlaceholder: 'start',
    targetHint: 'Ej: start, dev, serve — el nombre del script en tu package.json',
    buildDefault: 'npm ci',
    preview: (t) => `npm run ${t || 'start'}`,
  },
  {
    key: 'node',
    label: 'node [archivo]',
    description: 'Lanza un archivo .js directamente con Node.js',
    startMode: 'node',
    defaultTarget: 'index.js',
    targetLabel: 'Archivo de entrada',
    targetPlaceholder: 'index.js',
    targetHint: 'Ej: index.js, src/app.js, dist/server.js',
    buildDefault: 'npm ci',
    preview: (t) => `node ${t || 'index.js'}`,
  },
];

const PYTHON_PRESETS: StartPreset[] = [
  {
    key: 'python',
    label: 'Python script',
    description: 'Ejecuta un archivo .py directamente',
    startMode: 'python',
    defaultTarget: 'main.py',
    targetLabel: 'Archivo de entrada',
    targetPlaceholder: 'main.py',
    targetHint: 'Ej: main.py, app.py, server.py',
    buildDefault: 'pip install -r requirements.txt',
    preview: (t) => `python ${t || 'main.py'}`,
  },
  {
    key: 'gunicorn',
    label: 'Gunicorn (WSGI)',
    description: 'Servidor de producción para apps Flask / Django',
    startMode: 'gunicorn',
    defaultTarget: 'app:app',
    targetLabel: 'Módulo y callable',
    targetPlaceholder: 'app:app',
    targetHint: 'Flask: app:app  ·  Django: myproject.wsgi:application',
    buildDefault: 'pip install -r requirements.txt',
    preview: (t) => `gunicorn ${t || 'app:app'}`,
  },
  {
    key: 'uvicorn',
    label: 'Uvicorn (ASGI)',
    description: 'Servidor de producción para apps FastAPI / Starlette',
    startMode: 'uvicorn',
    defaultTarget: 'main:app',
    targetLabel: 'Módulo y callable',
    targetPlaceholder: 'main:app',
    targetHint: 'FastAPI: main:app  ·  Starlette: app:application',
    buildDefault: 'pip install -r requirements.txt',
    preview: (t) => `uvicorn ${t || 'main:app'} --host 0.0.0.0`,
  },
];

const GO_PRESETS: StartPreset[] = [
  {
    key: 'exec',
    label: 'Binario compilado',
    description: 'Ejecuta el binario ya compilado — recomendado para producción',
    startMode: 'exec',
    defaultTarget: './kubiy-app',
    targetLabel: 'Ruta del binario',
    targetPlaceholder: './kubiy-app',
    targetHint: 'Ej: ./kubiy-app, ./bin/api',
    buildDefault: 'go build -o kubiy-app .',
    preview: (t) => t || './kubiy-app',
  },
  {
    key: 'go',
    label: 'go run',
    description: 'Compila y ejecuta en un solo paso (útil en desarrollo)',
    startMode: 'go',
    defaultTarget: 'main.go',
    targetLabel: 'Archivo o paquete',
    targetPlaceholder: 'main.go',
    targetHint: 'Ej: main.go, ./cmd/server, .',
    buildDefault: '',
    preview: (t) => `go run ${t || 'main.go'}`,
  },
];

const DOTNET_PRESETS: StartPreset[] = [
  {
    key: 'auto',
    label: 'Auto-detect (recomendado)',
    description: 'Kubiy detecta el .csproj y lo ejecuta automáticamente',
    startMode: 'auto',
    defaultTarget: '',
    targetLabel: '',
    targetPlaceholder: '',
    targetHint: '',
    buildDefault: '',
    preview: () => 'dotnet run (auto-detect)',
  },
  {
    key: 'publish',
    label: 'dotnet publish',
    description: 'Publica la aplicación y ejecuta el binario resultante',
    startMode: 'publish',
    defaultTarget: '',
    targetLabel: 'Proyecto (opcional)',
    targetPlaceholder: 'MyApp.csproj',
    targetHint: 'Déjalo vacío para detectar automáticamente el .csproj en el root',
    buildDefault: '',
    preview: (t) => `dotnet publish${t ? ` ${t}` : ''} && ./app`,
  },
  {
    key: 'dotnet',
    label: 'dotnet [proyecto.dll]',
    description: 'Ejecuta un .dll ya compilado con el runtime de .NET',
    startMode: 'dotnet',
    defaultTarget: 'MyApp.dll',
    targetLabel: 'Ruta del .dll',
    targetPlaceholder: 'MyApp.dll',
    targetHint: 'Ruta relativa al .dll dentro del artefacto publicado',
    buildDefault: '',
    preview: (t) => `dotnet ${t || 'MyApp.dll'}`,
  },
  {
    key: 'dotnet-run',
    label: 'dotnet run',
    description: 'Compila y ejecuta en un solo paso (útil en desarrollo)',
    startMode: 'dotnet-run',
    defaultTarget: '',
    targetLabel: 'Proyecto (opcional)',
    targetPlaceholder: 'MyApp.csproj',
    targetHint: 'Déjalo vacío para usar el proyecto en el directorio actual',
    buildDefault: '',
    preview: (t) => `dotnet run${t ? ` --project ${t}` : ''}`,
  },
  {
    key: 'bin',
    label: 'Binario nativo',
    description: 'Ejecuta el ejecutable nativo generado por dotnet publish --self-contained',
    startMode: 'bin',
    defaultTarget: './MyApp',
    targetLabel: 'Ruta del ejecutable',
    targetPlaceholder: './MyApp',
    targetHint: 'Ruta al ejecutable self-contained generado por dotnet publish',
    buildDefault: '',
    preview: (t) => t || './MyApp',
  },
];

// ─── Helpers ──────────────────────────────────────────────

function getPresets(type: string): StartPreset[] {
  if (type === 'nodejs' || type === 'nextjs' || type === 'nuxt') return NODE_PRESETS;
  if (type === 'python') return PYTHON_PRESETS;
  if (type === 'go') return GO_PRESETS;
  if (type === 'netcore') return DOTNET_PRESETS;
  return [];
}

function getActivePreset(presets: StartPreset[], startMode?: string): StartPreset | null {
  if (!startMode) return null;
  return presets.find((p) => p.startMode === startMode) ?? null;
}

function getBuildDefault(type: string, startMode?: string): string {
  const presets = getPresets(type);
  const active = getActivePreset(presets, startMode);
  return active?.buildDefault ?? '';
}

// ─── Component ────────────────────────────────────────────

const Step2DeployApp: React.FC<Step2DeployAppProps> = ({
  config,
  setConfig,
  selectedLogsPlan,
  setSelectedLogsPlan,
  getRuntimeTechnology,
  getRuntimeVersions,
  isSSRTechnology,
  isSSGTechnology,
  backendProjectTypes,
}) => {
  const { t } = useTranslation();
  const [showAdvanced, setShowAdvanced] = useState(false);

  if (config.type === 'html-javascript-ai') {
    return <AITemplateGeneratorStep config={config} setConfig={setConfig} />;
  }

  const isBackend =
    (isSSRTechnology(config.type, config.framework) ||
      backendProjectTypes.some((bt) => bt.id === config.type)) &&
    !isSSGTechnology(config.type, config.framework);

  // Static apps — no config needed
  if (!isBackend) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
          <CheckCircleOutlined className="text-3xl text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {t('deployApp.step2.staticReady.title') || 'Tu aplicación estática está lista'}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
          {t('deployApp.step2.staticReady.description') ||
            'No se requiere configuración adicional para aplicaciones estáticas. Puedes continuar al siguiente paso.'}
        </p>
      </div>
    );
  }

  const runtimeTech = getRuntimeTechnology(config.type);
  const versions = getRuntimeVersions(runtimeTech);
  const presets = getPresets(config.type);
  const activePreset = getActivePreset(presets, config.startMode);

  const targetIsOptional =
    config.type === 'netcore' &&
    (config.startMode === 'auto' || config.startMode === 'publish' || !config.startMode);

  // Command preview string
  const commandPreview = activePreset
    ? activePreset.preview(config.targetMode ?? activePreset.defaultTarget)
    : null;

  const selectPreset = (preset: StartPreset) => {
    setConfig({
      ...config,
      startMode: preset.startMode,
      targetMode: preset.defaultTarget,
    });
  };

  const runtimeLabel = (() => {
    switch (config.type) {
      case 'nodejs': return t('deployApp.step2.runtime.label.nodejs') || 'Versión de Node.js';
      case 'netcore': return t('deployApp.step2.runtime.label.netcore') || 'Versión de .NET';
      case 'python': return t('deployApp.step2.runtime.label.python') || 'Versión de Python';
      case 'nextjs':
      case 'nuxt': return t('deployApp.step2.runtime.label.nodejs') || 'Versión de Node.js';
      default: return t('deployApp.step2.runtime.label.generic') || 'Versión del runtime';
    }
  })();

  return (
    <div className="space-y-8">

      {/* ── Runtime version ── */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {runtimeLabel} *
        </label>
        <select
          value={config.runtime?.version || ''}
          onChange={(e) =>
            setConfig({
              ...config,
              runtime: { technology: runtimeTech, version: e.target.value },
            })
          }
          required
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="">{t('deployApp.step2.runtime.placeholder') || 'Selecciona una versión...'}</option>
          {versions.map((v) => (
            <option key={v.value} value={v.value}>
              {v.label}
            </option>
          ))}
        </select>
        {versions.find((v) => v.recommended && v.value === config.runtime?.version) && (
          <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
            <CheckCircleOutlined /> {t('deployApp.step2.runtime.recommendedHint') || 'Versión recomendada para producción'}
          </p>
        )}
      </div>

      {/* ── Startup configuration ── */}
      <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-gray-700">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            ¿Cómo inicia tu aplicación? *
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Selecciona el método que usa tu app para arrancar.
          </p>
        </div>

        {/* Preset cards */}
        <div className="grid grid-cols-1 gap-2">
          {presets.map((preset) => {
            const isSelected = config.startMode === preset.startMode;
            return (
              <button
                key={preset.key}
                type="button"
                onClick={() => selectPreset(preset)}
                className={`w-full text-left px-4 py-3 border-2 rounded-lg transition-all ${
                  isSelected
                    ? 'border-black dark:border-white bg-gray-50 dark:bg-gray-800'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex-shrink-0 mt-0.5 ${
                      isSelected
                        ? 'border-black dark:border-white bg-black dark:bg-white'
                        : 'border-gray-300 dark:border-gray-500'
                    }`}
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white font-mono">
                      {preset.label}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {preset.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Target field — shown when preset is selected and has a target */}
        {activePreset && (activePreset.targetLabel || !targetIsOptional) && (
          <div className="space-y-1 pl-7">
            {activePreset.targetLabel && (
              <>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {activePreset.targetLabel}
                  {targetIsOptional && (
                    <span className="ml-1 text-xs font-normal text-gray-400">(opcional)</span>
                  )}
                  {!targetIsOptional && activePreset.targetLabel && ' *'}
                </label>
                <input
                  type="text"
                  value={config.targetMode ?? ''}
                  onChange={(e) => setConfig({ ...config, targetMode: e.target.value })}
                  placeholder={activePreset.targetPlaceholder}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                />
                {activePreset.targetHint && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">{activePreset.targetHint}</p>
                )}
              </>
            )}
          </div>
        )}

        {/* Live command preview */}
        {commandPreview && (
          <div className="pl-7">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Comando que ejecutaremos en tu servidor:
            </p>
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-900 dark:bg-black rounded-lg">
              <span className="text-green-400 text-xs select-none">$</span>
              <code className="text-sm text-green-300 font-mono">{commandPreview}</code>
            </div>
          </div>
        )}
      </div>

      {/* ── Advanced configuration (collapsible) ── */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Configuración avanzada
          </span>
          <span className="text-gray-500 dark:text-gray-400 text-xs">
            {showAdvanced ? <CaretDownOutlined /> : <CaretRightOutlined />}
          </span>
        </button>

        {showAdvanced && (
          <div className="px-4 py-4 space-y-5 border-t border-gray-200 dark:border-gray-700">

            {/* Build command */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Comando de build{' '}
                <span className="text-xs font-normal text-gray-400">(opcional)</span>
              </label>
              <input
                type="text"
                value={config.buildCommand ?? ''}
                onChange={(e) =>
                  setConfig({ ...config, buildCommand: e.target.value || undefined })
                }
                placeholder={getBuildDefault(config.type, config.startMode) || 'Ej: npm ci, pip install -r requirements.txt'}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Se ejecuta una vez antes de arrancar tu app — útil para instalar dependencias o compilar assets.
                {getBuildDefault(config.type, config.startMode) && (
                  <> Sugerencia por defecto: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{getBuildDefault(config.type, config.startMode)}</code></>
                )}
              </p>
            </div>

            {/* Port hint */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Puerto de escucha
              </label>
              <div className="flex items-center gap-3 px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                <span className="text-sm text-gray-700 dark:text-gray-300 font-mono">
                  {config.type === 'nodejs' || config.type === 'nextjs' || config.type === 'nuxt'
                    ? '3000'
                    : config.type === 'python'
                    ? '8000'
                    : config.type === 'go'
                    ? '8080'
                    : config.type === 'netcore'
                    ? '5000'
                    : '8080'}
                </span>
                <span className="text-xs text-gray-400">(Kubiy inyecta la variable de entorno <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">PORT</code> — tu app debe leerla)</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Asegúrate de que tu app escuche en <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">process.env.PORT</code> (Node) / <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">os.environ["PORT"]</code> (Python) en lugar de un puerto fijo.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── Logs ── */}
      <div className="pt-2 border-t border-gray-200 dark:border-gray-700 space-y-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          {t('deployApp.logs.title') || 'Logs y observabilidad'}
        </h3>

        {/* Standard logs (always included) */}
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-start gap-2">
            <CheckCircleOutlined className="text-green-600 dark:text-green-400 text-lg mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-green-900 dark:text-green-300 mb-1">
                {t('deployApp.logs.standard.title') || 'Logs estándar — incluido'}
              </h4>
              <p className="text-xs text-green-800 dark:text-green-200 mb-2">
                {t('deployApp.logs.standard.description') || 'Acceso a logs en tiempo real de tu aplicación sin costo adicional.'}
              </p>
              <ul className="text-xs text-green-700 dark:text-green-300 space-y-1">
                <li>• {t('deployApp.logs.standard.feature1') || 'Logs en tiempo real vía dashboard'}</li>
                <li>• {t('deployApp.logs.standard.feature2') || 'Retención de 1 día'}</li>
                <li>• {t('deployApp.logs.standard.feature3') || 'stdout + stderr de tu aplicación'}</li>
                <li>• {t('deployApp.logs.standard.feature4') || 'Alertas básicas de crashes'}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Premium logs (add-on) */}
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config.premiumLogs}
              onChange={(e) => {
                setConfig({ ...config, premiumLogs: e.target.checked });
                if (!e.target.checked) setSelectedLogsPlan(null);
              }}
              className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 accent-black dark:accent-white mt-0.5"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300">
                  {t('deployApp.logs.premium.title') || 'Logs premium'}
                </h4>
                <span className="text-xs px-2 py-0.5 bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded font-medium">
                  {t('deployApp.logs.premium.addon') || 'Add-on'}
                </span>
              </div>
              <p className="text-xs text-blue-800 dark:text-blue-200 mb-2">
                {t('deployApp.logs.premium.description') || 'Mayor retención, búsqueda avanzada y alertas personalizadas.'}
              </p>
              <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                <li>• {t('deployApp.logs.premium.feature1') || 'Retención extendida (7, 30 días o personalizada)'}</li>
                <li>• {t('deployApp.logs.premium.feature2') || 'Búsqueda full-text en logs históricos'}</li>
                <li>• {t('deployApp.logs.premium.feature3') || 'Alertas por patrones o palabras clave'}</li>
                <li>• {t('deployApp.logs.premium.feature4') || 'Exportación a S3 / sistemas externos'}</li>
                <li>• {t('deployApp.logs.premium.feature5') || 'Dashboard de métricas avanzado'}</li>
              </ul>
            </div>
          </label>

          {config.premiumLogs && (
            <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-800">
              <h5 className="text-xs font-semibold text-blue-900 dark:text-blue-300 mb-3">
                {t('deployApp.logs.premium.selectPlan') || 'Selecciona un plan de retención:'}
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  {
                    id: 'premium-7days',
                    name: t('deployApp.logs.premium.plans.plan1.name') || '7 días',
                    price: 5,
                    features: [
                      t('deployApp.logs.premium.plans.plan1.feature1') || 'Retención 7 días',
                      t('deployApp.logs.premium.plans.plan1.feature2') || 'Búsqueda y alertas',
                    ],
                  },
                  {
                    id: 'premium-30days',
                    name: t('deployApp.logs.premium.plans.plan2.name') || '30 días',
                    price: 10,
                    features: [
                      t('deployApp.logs.premium.plans.plan2.feature1') || 'Retención 30 días',
                      t('deployApp.logs.premium.plans.plan2.feature2') || 'Exportación incluida',
                    ],
                  },
                  {
                    id: 'premium-enterprise',
                    name: t('deployApp.logs.premium.plans.plan3.name') || 'Enterprise',
                    price: null,
                    features: [t('deployApp.logs.premium.plans.plan3.feature1') || 'Retención personalizada'],
                  },
                ].map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedLogsPlan(plan.id)}
                    className={`border-2 rounded-lg p-3 text-left transition-all ${
                      selectedLogsPlan === plan.id
                        ? 'border-black dark:border-white bg-blue-100 dark:bg-blue-900/30'
                        : 'border-blue-200 dark:border-blue-800 hover:border-blue-400 dark:hover:border-blue-600 bg-white dark:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h6 className="text-xs font-semibold text-blue-900 dark:text-blue-300">{plan.name}</h6>
                      {plan.price !== null ? (
                        <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                          ${plan.price}
                          <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                            {t('deployApp.logs.premium.perMonth') || '/mes'}
                          </span>
                        </span>
                      ) : (
                        <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                          {t('deployApp.logs.premium.plans.custom') || 'Custom'}
                        </span>
                      )}
                    </div>
                    <ul className="space-y-1">
                      {plan.features.map((f, i) => (
                        <li key={i} className="text-xs text-blue-700 dark:text-blue-300">• {f}</li>
                      ))}
                    </ul>
                    {selectedLogsPlan === plan.id && (
                      <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-800">
                        <span className="text-xs font-medium text-blue-600 dark:text-blue-400 flex items-center gap-1">
                          <CheckCircleOutlined /> {t('deployApp.availablePlans.selected') || 'Seleccionado'}
                        </span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* PORT tip */}
        <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <InfoCircleOutlined className="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-800 dark:text-amber-200">
            <strong>Importante:</strong> Tu aplicación debe escuchar en el puerto que Kubiy le asigne dinámicamente
            a través de la variable de entorno <code className="bg-amber-100 dark:bg-amber-800 px-1 rounded font-mono">PORT</code>.
            Evita hardcodear un puerto fijo.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Step2DeployApp;
